class KootaAnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-announcement-slide]'));
    this.track = this.querySelector('[data-announcement-track]');
    this.previousButton = this.querySelector('[data-announcement-previous]');
    this.nextButton = this.querySelector('[data-announcement-next]');
    this.autoplayButton = this.querySelector('[data-announcement-autoplay]');
    this.status = this.querySelector('[data-announcement-status]');
    this.currentIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.autoplayEnabled = this.dataset.autoplay === 'true' && this.slides.length > 1;
    this.pauseOnHover = this.dataset.pauseOnHover === 'true';
    this.speed = Math.max(5, Number(this.dataset.speed) || 7) * 1000;
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.motionOverride = false;
    this.temporaryPauseReasons = new Set();
    this.storageKey = `koota-announcement-paused-${this.dataset.sectionId || 'header'}`;
    this.userPaused = this.readPausedPreference();

    this.onPreviousClick = () => this.showPrevious();
    this.onNextClick = () => this.showNext();
    this.onAutoplayClick = () => this.toggleAutoplay();
    this.onPointerEnter = () => this.addTemporaryPause('hover');
    this.onPointerLeave = () => this.removeTemporaryPause('hover');
    this.onFocusIn = () => this.addTemporaryPause('focus');
    this.onFocusOut = (event) => {
      if (!this.contains(event.relatedTarget)) this.removeTemporaryPause('focus');
    };
    this.onVisibilityChange = () => {
      document.hidden ? this.addTemporaryPause('visibility') : this.removeTemporaryPause('visibility');
    };
    this.onMotionChange = () => this.handleMotionPreference();
    this.onBlockSelect = (event) => this.handleBlockSelect(event);
    this.onBlockDeselect = () => this.removeTemporaryPause('editor');
    this.onLinkClick = (event) => this.handleLinkClick(event);

    this.previousButton?.addEventListener('click', this.onPreviousClick);
    this.nextButton?.addEventListener('click', this.onNextClick);
    this.autoplayButton?.addEventListener('click', this.onAutoplayClick);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);
    this.addEventListener('shopify:block:select', this.onBlockSelect);
    this.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    this.addEventListener('click', this.onLinkClick);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    if (this.pauseOnHover) {
      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
    }

    if (typeof this.motionQuery.addEventListener === 'function') {
      this.motionQuery.addEventListener('change', this.onMotionChange);
    } else {
      this.motionQuery.addListener(this.onMotionChange);
    }

    this.updateSlides();
    this.updateAutoplayControl();
    this.startAutoplay();
  }

  disconnectedCallback() {
    window.clearTimeout(this.autoplayTimer);
    window.clearTimeout(this.liveRegionTimer);
    this.previousButton?.removeEventListener('click', this.onPreviousClick);
    this.nextButton?.removeEventListener('click', this.onNextClick);
    this.autoplayButton?.removeEventListener('click', this.onAutoplayClick);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    this.removeEventListener('click', this.onLinkClick);
    this.removeEventListener('pointerenter', this.onPointerEnter);
    this.removeEventListener('pointerleave', this.onPointerLeave);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);

    if (typeof this.motionQuery.removeEventListener === 'function') {
      this.motionQuery.removeEventListener('change', this.onMotionChange);
    } else {
      this.motionQuery.removeListener(this.onMotionChange);
    }
  }

  showPrevious() {
    this.showSlide(this.currentIndex - 1, { manual: true });
  }

  showNext() {
    this.showSlide(this.currentIndex + 1, { manual: true });
  }

  showSlide(index, { manual = false } = {}) {
    if (this.slides.length < 2) return;

    this.currentIndex = (index + this.slides.length) % this.slides.length;
    this.updateSlides();

    if (manual) this.announceCurrentSlide();

    window.clearTimeout(this.autoplayTimer);
    this.startAutoplay();
  }

  updateSlides() {
    this.slides.forEach((slide, index) => {
      const isActive = index === this.currentIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));

      slide.querySelectorAll('a, button').forEach((element) => {
        if (isActive) {
          element.removeAttribute('tabindex');
        } else {
          element.setAttribute('tabindex', '-1');
        }
      });
    });
  }

  announceCurrentSlide() {
    if (!this.status) return;

    const currentText = this.slides[this.currentIndex]?.querySelector('.koota-announcement__text')?.textContent?.trim();
    this.status.textContent = `Message ${this.currentIndex + 1} sur ${this.slides.length}${currentText ? ` : ${currentText}` : ''}`;

    window.clearTimeout(this.liveRegionTimer);
    this.liveRegionTimer = window.setTimeout(() => {
      this.status.textContent = '';
    }, 3000);
  }

  startAutoplay() {
    window.clearTimeout(this.autoplayTimer);

    if (!this.canAutoplay()) return;

    this.autoplayTimer = window.setTimeout(() => {
      this.showSlide(this.currentIndex + 1);
    }, this.speed);
  }

  stopAutoplay() {
    window.clearTimeout(this.autoplayTimer);
  }

  canAutoplay() {
    if (!this.autoplayEnabled || this.userPaused || this.temporaryPauseReasons.size > 0 || document.hidden) return false;
    if (this.motionQuery.matches && !this.motionOverride) return false;
    return true;
  }

  toggleAutoplay() {
    const isPaused = this.userPaused || (this.motionQuery.matches && !this.motionOverride);

    if (isPaused) {
      this.userPaused = false;
      this.motionOverride = true;
      this.savePausedPreference(false);
      this.startAutoplay();
    } else {
      this.userPaused = true;
      this.savePausedPreference(true);
      this.stopAutoplay();
    }

    this.updateAutoplayControl();
  }

  updateAutoplayControl() {
    if (!this.autoplayButton) return;

    const isPaused = this.userPaused || (this.motionQuery.matches && !this.motionOverride);
    this.autoplayButton.classList.toggle('is-paused', isPaused);
    this.autoplayButton.setAttribute('aria-pressed', String(isPaused));
    this.autoplayButton.setAttribute('aria-label', isPaused ? 'Reprendre le défilement' : 'Mettre le défilement en pause');
  }

  addTemporaryPause(reason) {
    this.temporaryPauseReasons.add(reason);
    this.stopAutoplay();
  }

  removeTemporaryPause(reason) {
    this.temporaryPauseReasons.delete(reason);
    this.startAutoplay();
  }

  handleMotionPreference() {
    this.motionOverride = false;
    this.updateAutoplayControl();

    if (this.motionQuery.matches) {
      this.stopAutoplay();
    } else {
      this.startAutoplay();
    }
  }

  handleBlockSelect(event) {
    const blockId = event.detail?.blockId;
    const selectedIndex = this.slides.findIndex((slide) => slide.dataset.announcementId === blockId);

    this.addTemporaryPause('editor');
    if (selectedIndex >= 0) this.showSlide(selectedIndex, { manual: true });
  }

  handleLinkClick(event) {
    const link = event.target.closest?.('[data-announcement-link]');
    if (!link) return;

    const slide = link.closest('[data-announcement-slide]');
    this.dispatchEvent(new CustomEvent('koota:announcement-click', {
      bubbles: true,
      detail: {
        blockId: slide?.dataset.announcementId || '',
        index: this.currentIndex,
        text: slide?.querySelector('.koota-announcement__text')?.textContent?.trim() || '',
        url: link.getAttribute('href') || '',
      },
    }));
  }

  readPausedPreference() {
    try {
      return window.sessionStorage.getItem(this.storageKey) === 'true';
    } catch (error) {
      return false;
    }
  }

  savePausedPreference(isPaused) {
    try {
      if (isPaused) {
        window.sessionStorage.setItem(this.storageKey, 'true');
      } else {
        window.sessionStorage.removeItem(this.storageKey);
      }
    } catch (error) {
      // Storage can be unavailable in private browsing; the control still works for the current page.
    }
  }
}

if (!customElements.get('koota-announcement-bar')) {
  customElements.define('koota-announcement-bar', KootaAnnouncementBar);
}
